import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  DollarSign, 
  ArrowLeft, 
  RotateCcw, 
  ArrowRightLeft, 
  TrendingUp, 
  Globe, 
  CheckCircle2, 
  Sparkles 
} from 'lucide-react';

export const CurrencyConverter = () => {
  const { withAd, toast } = useAdRedirect();
  const [amount, setAmount] = useState('100');
  const [fromCurr, setFromCurr] = useState('USD');
  const [toCurr, setToCurr] = useState('EUR');

  // Simulated live exchange rates base parity against USD
  const ratesPool = {
    USD: 1.0,
    EUR: 0.92,
    GBP: 0.79,
    JPY: 155.40,
    CAD: 1.37,
    AUD: 1.52,
    INR: 83.50,
    CNY: 7.24,
    CHF: 0.91,
    SGD: 1.35,
    HKD: 7.82,
    NZD: 1.66,
    ZAR: 18.40,
    MXN: 16.85,
    BRL: 5.15,
    RUB: 91.50,
    KRW: 1365.0,
    SEK: 10.85,
    NOK: 10.95,
    DKK: 6.88,
    TRY: 32.20,
    IDR: 16050.0,
    MYR: 4.74,
    PHP: 57.50,
    THB: 36.80,
    VND: 25440.0,
    PKR: 278.0,
    BDT: 117.0,
    EGP: 47.30,
    NGN: 1460.0,
    KES: 131.50,
    SAR: 3.75,
    AED: 3.67,
    QAR: 3.64,
    KWD: 0.31,
    ILS: 3.72,
    PLN: 3.96,
    CZK: 22.90,
    HUF: 358.50,
    RON: 4.61,
    COP: 3890.0,
    CLP: 935.0,
    ARS: 885.0,
    PEN: 3.73,
    TWD: 32.40,
    LKR: 300.0,
    MAD: 10.05,
    DZD: 134.50,
    GHS: 14.20,
    TZS: 2590.0,
    UAH: 39.60,
    KZT: 441.50,
    UZS: 12700.0
  };

  const currNames = {
    USD: 'United States Dollar ($)',
    EUR: 'Euro (€)',
    GBP: 'British Pound (£)',
    JPY: 'Japanese Yen (¥)',
    CAD: 'Canadian Dollar (C$)',
    AUD: 'Australian Dollar (A$)',
    INR: 'Indian Rupee (₹)',
    CNY: 'Chinese Yuan (CN¥)',
    CHF: 'Swiss Franc (CHF)',
    SGD: 'Singapore Dollar (S$)',
    HKD: 'Hong Kong Dollar (HK$)',
    NZD: 'New Zealand Dollar (NZ$)',
    ZAR: 'South African Rand (R)',
    MXN: 'Mexican Peso (Mex$)',
    BRL: 'Brazilian Real (R$)',
    RUB: 'Russian Ruble (₽)',
    KRW: 'South Korean Won (₩)',
    SEK: 'Swedish Krona (kr)',
    NOK: 'Norwegian Krone (kr)',
    DKK: 'Danish Krone (kr)',
    TRY: 'Turkish Lira (₺)',
    IDR: 'Indonesian Rupiah (Rp)',
    MYR: 'Malaysian Ringgit (RM)',
    PHP: 'Philippine Peso (₱)',
    THB: 'Thai Baht (฿)',
    VND: 'Vietnamese Dong (₫)',
    PKR: 'Pakistani Rupee (₨)',
    BDT: 'Bangladeshi Taka (৳)',
    EGP: 'Egyptian Pound (E£)',
    NGN: 'Nigerian Naira (₦)',
    KES: 'Kenyan Shilling (KSh)',
    SAR: 'Saudi Riyal (SR)',
    AED: 'United Arab Emirates Dirham (AED)',
    QAR: 'Qatari Riyal (QR)',
    KWD: 'Kuwaiti Dinar (KD)',
    ILS: 'Israeli New Shekel (₪)',
    PLN: 'Polish Zloty (zł)',
    CZK: 'Czech Koruna (Kč)',
    HUF: 'Hungarian Forint (Ft)',
    RON: 'Romanian Leu (lei)',
    COP: 'Colombian Peso (Col$)',
    CLP: 'Chilean Peso (CLP$)',
    ARS: 'Argentine Peso (Arg$)',
    PEN: 'Peruvian Sol (S/)',
    TWD: 'New Taiwan Dollar (NT$)',
    LKR: 'Sri Lankan Rupee (LKR)',
    MAD: 'Moroccan Dirham (MAD)',
    DZD: 'Algerian Dinar (DZD)',
    GHS: 'Ghanaian Cedi (GH₵)',
    TZS: 'Tanzanian Shilling (TZS)',
    UAH: 'Ukrainian Hryvnia (₴)',
    KZT: 'Kazakhstani Tenge (₸)',
    UZS: 'Uzbekistani Som (UZS)'
  };

  const convertCurr = () => {
    let amtNum = parseFloat(amount);
    if (!amtNum || isNaN(amtNum)) return { converted: 0, rate: 0 };

    let fromRate = ratesPool[fromCurr];
    let toRate = ratesPool[toCurr];

    // Conversion factor
    let factor = toRate / fromRate;
    let converted = parseFloat((amtNum * factor).toFixed(2));
    let rate = parseFloat(factor.toFixed(4));

    return { converted, rate };
  };

  const conversion = convertCurr();

  const handleSwap = () => {
    const temp = fromCurr;
    setFromCurr(toCurr);
    setToCurr(temp);
  };

  const handleReset = () => {
    setAmount('100');
    setFromCurr('USD');
    setToCurr('EUR');
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', paddingBottom: '60px' }}>
      <SEO 
        title="Free Live Currency Converter & Exchange Rates" 
        description="Convert between USD, EUR, GBP, JPY, CAD, AUD, INR, and global currencies instantly. Check live simulated foreign exchange rate trends."
        keywords="currency converter, live exchange rates, forex rate converter, usd to eur, gbp to usd, money converter online"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '0.9rem' }}>
        <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Currency Converter</span>
      </div>

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
          Live Currency Converter & Foreign Exchange
        </h1>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0 }}>
          Convert fiat monetary amounts across global trading pairs instantly with accurate live simulation spreads.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }} className="tool-grid-split">
        
        {/* Left Side: Inputs */}
        <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)', alignSelf: 'start' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <DollarSign size={18} style={{ color: 'var(--primary)' }} /> Conversion Spreads
            </h3>
            <button onClick={handleReset} style={{ background: 'rgba(15, 23, 42, 0.04)', border: 'none', padding: '6px 12px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <RotateCcw size={14} /> Clear
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Amount</label>
              <input 
                type="number" 
                value={amount} 
                onChange={(e) => setAmount(e.target.value)}
                placeholder="100"
                style={{ width: '100%', padding: '14px 16px', borderRadius: '12px', border: '1px solid var(--border-color)', fontSize: '1.1rem', fontWeight: 700, outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>From Currency</label>
                <select 
                  value={fromCurr} 
                  onChange={(e) => setFromCurr(e.target.value)}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', fontWeight: 600, outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                >
                  {Object.keys(ratesPool).map(code => (
                    <option key={code} value={code}>{code} - {currNames[code]}</option>
                  ))}
                </select>
              </div>

              <button 
                type="button" 
                onClick={handleSwap}
                style={{ alignSelf: 'flex-end', background: 'rgba(5, 117, 251, 0.1)', color: 'var(--primary)', border: 'none', padding: '12px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                title="Swap Currencies"
              >
                <ArrowRightLeft size={18} />
              </button>

              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>To Currency</label>
                <select 
                  value={toCurr} 
                  onChange={(e) => setToCurr(e.target.value)}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', fontWeight: 600, outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                >
                  {Object.keys(ratesPool).map(code => (
                    <option key={code} value={code}>{code} - {currNames[code]}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>Real-time Forex Parity</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Simulated foreign exchange liquidity rates are calculated client-side for rapid benchmark estimation.</div>
            </div>
          </div>
        </div>

        {/* Right Side: Dashboard */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Globe size={18} style={{ color: '#10b981' }} /> Converted Amount Dashboard
            </h3>

            <div>
              {/* Primary Display */}
              <div style={{ background: 'var(--bg-secondary)', padding: '28px', borderRadius: '16px', border: '1px solid var(--border-color)', textAlign: 'center', marginBottom: '20px' }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  {amount || 0} {fromCurr} equals
                </div>
                <div style={{ fontSize: '3rem', fontWeight: 900, color: 'var(--primary)', lineHeight: 1.1, marginBottom: '6px' }}>
                  {conversion.converted.toLocaleString()} {toCurr}
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  1 {fromCurr} = {conversion.rate} {toCurr}
                </div>
              </div>

              {/* Quick Conversion Table */}
              <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '20px' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <TrendingUp size={16} style={{ color: '#10b981' }} /> Popular {fromCurr} to {toCurr} Quantities
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                  {[10, 50, 100, 500, 1000, 5000, 10000, 50000].map(val => {
                    let conv = parseFloat((val * conversion.rate).toFixed(2));
                    return (
                      <div key={val} style={{ background: 'var(--bg-secondary)', padding: '10px', borderRadius: '10px', border: '1px solid var(--border-color)', textAlign: 'center', fontSize: '0.8rem' }}>
                        <div style={{ fontWeight: 700, color: 'var(--text-secondary)' }}>{val} {fromCurr}</div>
                        <div style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{conv.toLocaleString()} {toCurr}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={{ marginTop: '28px', textAlign: 'center' }}>
              <AdToast message={toast} />
              <button 
                onClick={withAd('currency-convert-btn', () => alert("Rates locked client-side."))}
                style={{ background: 'var(--primary)', color: '#ffffff', border: 'none', padding: '12px 28px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', transition: 'var(--transition-smooth)' }}
              >
                Log Exchange Conversion
              </button>
            </div>
          </div>

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .tool-grid-split { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      <AdUnit />
      <ToolPageExtras toolId="currency-converter" />
    </div>
  );
};

export default CurrencyConverter;
